<script setup lang="ts">
// Overrides @basecom-gmbh/pond/components/Layout/Header/Search/LayoutHeaderSearchSuggestions.vue
// IDS-124: change style of products, change order of attributes

import { getProductRoute, getTranslatedProperty } from '@shopware-pwa/helpers-next';
import type { Schemas } from '@shopware/api-client/api-types';

const props = defineProps<{ product: Schemas['Product'] }>();
const { product } = toRefs(props);
const { isAddToCartAvailable } = useAddToCartAvailability(props.product);

const { getProductCover } = useMedia();
const productCover = getProductCover(props.product.cover?.media, 'xs');

const { t } = useI18n();
</script>

<template>
    <div class="bg-white my-1 cursor-pointer rounded p-3.5 transition duration-300 hover:bg-neutral-250">
        <div class="flex gap-4 text-sm">
            <div class="h-26 w-26 min-w-26 border border-neutral-300 md:h-18 md:w-18 md:min-w-18">
                <SharedImagePlaceholder
                    v-if="productCover.placeholder"
                    :size="'xs'"
                />

                <template v-else>
                    <img
                        loading="lazy"
                        :src="productCover.url"
                        class="object-contain h-full w-full"
                        :alt="productCover.alt ?? getTranslatedProperty(product, 'name')"
                        :title="productCover.title ?? getTranslatedProperty(product, 'name')"
                    />
                </template>
            </div>

            <div class="flex grow justify-between gap-5">
                <div class="flex flex-col">
                    <span class="text-neutral-600">
                        {{ product.productNumber }}
                    </span>

                    <span class="line-clamp-2 overflow-hidden text-ellipsis font-extrabold md:line-clamp-1">
                        {{ product.translated.name }}
                    </span>

                    <div class="mt-1 flex flex-wrap gap-2">
                        <span
                            v-for="option in product.options"
                            :key="option.id"
                            class="inline-block rounded bg-gray-light px-2 py-[3px] font-main text-xs font-normal leading-[18px] text-neutral-800"
                        >
                            {{
                                `${getTranslatedProperty(option.group, 'name')}: ${getTranslatedProperty(option, 'name')}`
                            }}
                        </span>
                    </div>
                </div>

                <div class="flex-none self-center text-right md:pr-4">
                    <!-- IDS-280 show custom template -->
                    <ProductPrice
                        v-if="isAddToCartAvailable"
                        :product="product"
                        font-size="text-sm"
                        :show-list-price-on-top="true"
                        msrp-text-class="text-xs mr-[3px]"
                        msrp-price-class="font-extrabold"
                    >
                        <template #priceHidden>
                            <div class="text-xs">
                                {{ t('global.price.loginNeeded.priceAfter') }}
                                <br />
                                <LocaleLink
                                    :to="`/account/login?redirect=${getProductRoute(product).path}`"
                                    class="underline"
                                >
                                    {{ t('global.price.loginNeeded.registration') }}
                                </LocaleLink>
                            </div>
                        </template>
                    </ProductPrice>

                    <p
                        v-else
                        class="text-sm text-neutral-600 w-20 md:w-40 text-left"
                    >
                        {{ t('product.unavailable') }}
                    </p>
                </div>
            </div>
        </div>
    </div>
    <div class="mx-3.5 border-b border-neutral-400" />
</template>
